import {FC, ReactElement} from 'react'
import {useAuth} from './Auth'
import {WithChildren} from '../../../../_metronic/helpers'

/**
 * Reads the current user's permission codes (from `/wms/auth/me/`) and exposes
 * helpers to check them.
 */
export function usePermissions() {
  const {currentUser} = useAuth()
  const permissions = currentUser?.permissions ?? []

  const has = (code: string) => permissions.includes(code)
  const hasAny = (codes: string[]) => codes.some(has)
  const hasAll = (codes: string[]) => codes.every(has)

  return {permissions, has, hasAny, hasAll}
}

type CanProps = {
  /** Single permission code required. */
  permission?: string
  /** Access granted if the user has ANY of these codes. */
  anyOf?: string[]
  /** Access granted only if the user has ALL of these codes. */
  allOf?: string[]
  /** Rendered when access is denied (defaults to nothing). */
  fallback?: ReactElement | null
}

/**
 * Conditionally renders its children based on the current user's permissions.
 *   <Can permission='wms:settings:manage'>...</Can>
 */
export const Can: FC<CanProps & WithChildren> = ({
  permission,
  anyOf,
  allOf,
  fallback = null,
  children,
}) => {
  const {has, hasAny, hasAll} = usePermissions()
  const allowed =
    (permission ? has(permission) : true) &&
    (anyOf ? hasAny(anyOf) : true) &&
    (allOf ? hasAll(allOf) : true)

  return <>{allowed ? children : fallback}</>
}

type RequirePermissionProps = {
  /** Permission code required to access the route. */
  permission?: string
  /** Element to render when the permission is missing (e.g. <Navigate />). */
  fallback: ReactElement
}

/**
 * Route guard — renders children only when the user has `permission`, otherwise
 * renders `fallback` (typically a <Navigate /> redirect).
 */
export const RequirePermission: FC<RequirePermissionProps & WithChildren> = ({
  permission,
  fallback,
  children,
}) => {
  const {has} = usePermissions()
  if (permission && !has(permission)) {
    return fallback
  }
  return <>{children}</>
}
